import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  Check, 
  X, 
  Rocket,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  Minimize2,
  Download
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useGettingStartedData } from "../getting-started-guide/shared/useGettingStartedData";
import { calculateGettingStartedProgress, getStepsWithStatus } from "../getting-started-guide/shared/gettingStartedUtils";

const COLLAPSED_STORAGE_KEY = "wealthbee-floating-guide-collapsed";

interface FloatingGettingStartedGuideProps {
  onDismiss?: () => void;
  onMinimize?: () => void;
}

/**
 * A floating getting started guide that appears as a persistent sidebar
 * Follows the user around the application until dismissed or completed
 * More compact version of the main getting started guide
 */
export const FloatingGettingStartedGuide = ({
  onDismiss,
  onMinimize,
}: FloatingGettingStartedGuideProps) => {
  const navigate = useNavigate();
  const { data, isLoading } = useGettingStartedData();
  const progress = calculateGettingStartedProgress(data);
  const stepsWithStatus = getStepsWithStatus(data);
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  });

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    localStorage.setItem(COLLAPSED_STORAGE_KEY, newCollapsed.toString());
  };

  const StepItem = ({ step }: { step: any }) => {
    const Icon = step.icon;
    const isNext = progress.nextStep?.id === step.id;
    
    return (
      <div 
        className={`flex items-center gap-3 p-2 rounded-lg transition-all cursor-pointer hover:bg-muted/50 ${
          step.completed 
            ? 'bg-green-50/50 dark:bg-green-950/20' 
            : isNext 
              ? 'bg-primary/5 border border-primary/20' 
              : step.enabled 
                ? 'hover:bg-muted/30' 
                : 'opacity-50'
        }`}
        onClick={() => step.enabled && !step.completed && navigate(step.route)}
      >
        <div className={`flex-shrink-0 h-7 w-7 rounded-lg flex items-center justify-center text-xs font-medium ${
          step.completed 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
            : isNext 
              ? 'bg-primary text-primary-foreground' 
              : step.enabled 
                ? 'bg-primary/10 text-primary' 
                : 'bg-muted text-muted-foreground'
        }`}>
          {step.completed ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className={`text-xs font-medium truncate ${
              step.completed ? 'text-green-700 dark:text-green-300' : 'text-foreground'
            }`}>
              {isCollapsed ? step.shortTitle : step.title}
            </p>
            {isNext && !isCollapsed && (
              <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium">
                Next
              </span>
            )}
          </div>
          {!isCollapsed && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground truncate">
                {step.description}
              </p>
              {step.mockDownloads && step.mockDownloads.length > 0 && (
                <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <Download className="h-2 w-2" />
                  Templates
                </span>
              )}
            </div>
          )}
        </div>
        
        {step.enabled && !step.completed && !isCollapsed && (
          <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        )}
      </div>
    );
  };

  return (
    <div className="fixed top-20 right-4 z-50 max-w-sm">
      <div className={`bg-card border rounded-xl shadow-lg transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-80'
      }`}>
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                <Rocket className="h-4 w-4" />
              </div>
              {!isCollapsed && (
                <div>
                  <h3 className="font-semibold text-sm">Setup Guide</h3>
                  <p className="text-xs text-muted-foreground">
                    {progress.completedCount}/{progress.totalCount} complete
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleCollapse}
                className="h-6 w-6"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </Button>
              {!isCollapsed && (
                <>
                  {onMinimize && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onMinimize}
                      className="h-6 w-6"
                    >
                      <Minimize2 className="h-3 w-3" />
                    </Button>
                  )}
                  {onDismiss && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onDismiss}
                      className="h-6 w-6"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          
          {!isCollapsed && (
            <div className="mt-3">
              <Progress value={progress.progressPercentage} className="h-2" />
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="text-muted-foreground">
                  {progress.nextStep ? `Next: ${progress.nextStep.shortTitle}` : 'All done!'}
                </span>
                <span className="text-muted-foreground font-medium">
                  {Math.round(progress.progressPercentage)}%
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Steps */}
        {!isCollapsed && (
          <div className="p-3 space-y-1 max-h-96 overflow-y-auto">
            {stepsWithStatus.map((step) => (
              <StepItem key={step.id} step={step} />
            ))}
          </div>
        )}

        {/* Collapsed view - show only next step */}
        {isCollapsed && progress.nextStep && (
          <div className="p-2">
            <StepItem step={progress.nextStep} />
          </div>
        )}

        {/* Quick action button */}
        {!isCollapsed && progress.nextStep && (
          <div className="p-4 border-t">
            <Button 
              size="sm" 
              className="w-full" 
              onClick={() => navigate(progress.nextStep!.route)}
            >
              Continue Setup
              <ArrowRight className="ml-2 h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Completion message */}
        {!isCollapsed && progress.isComplete && (
          <div className="p-4 border-t bg-green-50/50 dark:bg-green-950/20">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm font-medium">Setup Complete!</span>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              Your platform is ready to use.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}; 